import { useState } from 'react';

import { InfoModal } from '@backrs_ui/common-ui';

import { StoryDocument } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { ENVIRONMENT_URL } from '@backrs_ui/grow-ui/src/settings';
import { getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';

import { Button, Typography, Box, Dialog, Theme, SxProps } from '@mui/material';

type ConfirmDeleteProps = Readonly<{
  openDialog: boolean;
  setOpenDialog: (open: boolean) => void;
  selectedDocument: StoryDocument | null;
  onSuccess: (deletedId: number) => void;
}>;

export const ConfirmDelete = ({
  openDialog,
  setOpenDialog,
  selectedDocument,
  onSuccess,
}: ConfirmDeleteProps) => {
  const [error, setError] = useState<string | null>(null);

  const handleConfirmDelete = async () => {
    if (!selectedDocument) return;

    const token = getBackrsToken();

    const id =
      selectedDocument?.category_id === 'video'
        ? selectedDocument.id
        : selectedDocument.profile_option_answers_id;

    const response = await fetch(`${ENVIRONMENT_URL}/users-profile-answers/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      onSuccess(selectedDocument.id);
      setError(null);
      setOpenDialog(false);
    } else {
      setError('We ran into an error deleting this story. Please try again.');
    }
  };

  const handleCancel = () => {
    setOpenDialog(false);
    setError(null);
  };

  return (
    <Dialog open={openDialog} onClose={handleCancel}>
      <InfoModal
        icon="Trash01"
        headerContent={
          <Box sx={headerContainerSx}>
            <Typography variant="text-lg-semibold">Delete story</Typography>
            <Typography variant="text-sm-regular">
              Are you sure you want to delete this story?
            </Typography>
          </Box>
        }
        bodyContent={
          <Box sx={bodyContainerSx}>
            <Box sx={buttonContainerSx}>
              <Button onClick={handleCancel} hierarchy="Secondary gray" size="lg" fullWidth>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                hierarchy="Primary"
                size="lg"
                fullWidth
                sx={deleteButtonSx}
              >
                Delete
              </Button>
            </Box>
            {error && (
              <Typography
                variant="text-md-regular"
                color={({ backrs }) => backrs.colors['Colors/Text/text-error-primary']}
                mt={3}
              >
                {error}
              </Typography>
            )}
          </Box>
        }
        onClose={handleCancel}
        open={openDialog}
        width="sm"
      />
    </Dialog>
  );
};

// Shared Styles
const headerContainerSx: SxProps<Theme> = {
  zIndex: 100,
  position: 'relative',
};

const bodyContainerSx: SxProps<Theme> = {
  zIndex: 100,
  position: 'relative',
};

const buttonContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  alignSelf: 'flex-end',
  mt: backrs.spacing['spacing-2xl'],
});

const deleteButtonSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-error-solid'],
  color: backrs.colors['Colors/Base/White'],
  '&:hover': {
    backgroundColor: backrs.colors['Error/700'],
    transform: 'none',
    boxShadow: 'none',
  },
});
